import React, { useCallback, useEffect, useState } from 'react';
import { EmblaCarouselType } from 'embla-carousel';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { IconButtonProps } from '@mui/material';
import { StyledIconButton } from '../style';

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

export const usePrevNextButtons = (emblaApi: EmblaCarouselType | undefined): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);

  const getScrollContainer = useCallback(() => {
    if (!emblaApi || !emblaApi.rootNode) return null;
    return emblaApi.rootNode();
  }, [emblaApi]);

  const scrollByAmount = useCallback(
    (amount: number) => {
      const scrollContainer = getScrollContainer();
      if (!scrollContainer) return;
      scrollContainer.scrollBy({ left: amount, behavior: 'smooth' });
    },
    [getScrollContainer],
  );

  const onPrevButtonClick = useCallback(() => {
    const container = getScrollContainer();
    if (!container) return;
    scrollByAmount(-container.clientWidth);
  }, [getScrollContainer, scrollByAmount]);

  const onNextButtonClick = useCallback(() => {
    const container = getScrollContainer();
    if (!container) return;
    scrollByAmount(container.clientWidth);
  }, [getScrollContainer, scrollByAmount]);

  const updateButtons = useCallback(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer || !emblaApi) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const tolerance = 2;

    const atStart = scrollLeft <= tolerance && !emblaApi.canScrollPrev();
    const atEnd = scrollLeft + clientWidth >= scrollWidth - tolerance || !emblaApi.canScrollNext();

    setPrevBtnDisabled(atStart);
    setNextBtnDisabled(atEnd);
  }, [getScrollContainer, emblaApi]);

  useEffect(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer || !emblaApi) return;

    updateButtons();

    scrollContainer.addEventListener('scroll', updateButtons);

    const emblaEvents: Array<'select' | 'reInit' | 'settle'> = ['select', 'reInit', 'settle'];

    emblaEvents.forEach((event) => emblaApi.on(event, updateButtons));

    return () => {
      scrollContainer.removeEventListener('scroll', updateButtons);
      emblaEvents.forEach((event) => emblaApi.off(event, updateButtons));
    };
  }, [emblaApi, getScrollContainer, updateButtons]);
  
  

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

interface CarouselButtonProps extends Omit<IconButtonProps, 'onPress'> {
  onPress?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const PrevButton: React.FC<CarouselButtonProps> = (props) => {
  const { children, onPress, ...restProps } = props;

  return (
    <StyledIconButton onClick={onPress} position={'left'} {...restProps}>
      <ChevronLeft style={{color:'#000000'}}/>
      {children}
    </StyledIconButton>
  );
};

export const NextButton: React.FC<CarouselButtonProps> = (props) => {
  const { children, onPress, ...restProps } = props;

  return (
    <StyledIconButton onClick={onPress} position={'right'} {...restProps}>
      <ChevronRight style={{color:'#000000'}}/>
      {children}
    </StyledIconButton>
  );
};
